import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FiArrowLeft, FiAward, FiClock, FiCheckCircle, FiTrendingUp, FiTarget,
  FiBookOpen, FiStar, FiArrowRight, FiInfo,
} from 'react-icons/fi';
import { getMyCareerPath } from '../services/api';
import {
  CAREER_TRACKS,
  GROUP_STYLE,
  PayTierTable,
  formatTierBracket,
} from '../components/CareerBadge';

// Personalized career-path explainer page. Shows where the user is, how they
// got there, what's next, and the rules of the road (interweaving, Dual-Track
// Bracket Rule). Replaces 22 lessons of welcome-content reading with a single
// page they can act on.

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString();
}

function formatDays(days) {
  if (days == null) return '—';
  if (days < 30) return `${days} day${days === 1 ? '' : 's'}`;
  if (days < 365) {
    const m = Math.floor(days / 30);
    return `${m} month${m === 1 ? '' : 's'}`;
  }
  const y = Math.floor(days / 365);
  const remM = Math.floor((days % 365) / 30);
  return remM > 0 ? `${y}y ${remM}m` : `${y} year${y === 1 ? '' : 's'}`;
}

function Section({ title, icon: Icon, color = '#0f172a', children, intro }) {
  return (
    <section style={{ marginBottom: 32 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        {Icon && <Icon size={22} style={{ color }} />}
        <h2 style={{ margin: 0, color: '#0f172a' }}>{title}</h2>
      </div>
      {intro && (
        <p style={{ color: '#64748b', fontSize: 14, marginTop: 0, marginBottom: 14 }}>{intro}</p>
      )}
      <div>{children}</div>
    </section>
  );
}

function Card({ children, color = '#e2e8f0', bg = 'white', style: extraStyle = {} }) {
  return (
    <div style={{
      background: bg,
      border: `1px solid ${color}`,
      borderRadius: 10,
      padding: 18,
      ...extraStyle,
    }}>{children}</div>
  );
}

function CertBadge({ cert }) {
  const style = CAREER_TRACKS[cert.track] || { color: '#475569', bg: '#f1f5f9' };
  return (
    <div title={`${cert.title} — ${cert.score}%`} style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      background: style.bg,
      color: style.color,
      padding: '6px 12px',
      borderRadius: 16,
      fontSize: 13,
      fontWeight: 600,
      marginRight: 8,
      marginBottom: 8,
      opacity: cert.approved ? 1 : 0.7,
    }}>
      <FiAward size={14} />
      {cert.track} L{cert.level}
      {!cert.approved && (
        <span style={{ fontSize: 10, fontWeight: 400, opacity: 0.8 }}>(pending approval)</span>
      )}
    </div>
  );
}

function NextStepCard({ slot, label, color }) {
  if (!slot) return null;
  const eligibleColor = slot.eligible ? '#16a34a' : color;
  return (
    <Card color={slot.eligible ? '#bbf7d0' : '#cbd5e1'} bg={slot.eligible ? '#f0fdf4' : 'white'}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        {slot.eligible ? <FiCheckCircle color="#16a34a" /> : <FiClock color={eligibleColor} />}
        <span style={{ fontWeight: 700, fontSize: 14, color: '#0f172a' }}>{label}</span>
        {slot.bracket && (
          <span style={{ background: '#0f172a', color: 'white', padding: '2px 8px', borderRadius: 8, fontSize: 11 }}>
            {slot.bracket}
          </span>
        )}
        {slot.tier != null && (
          <span style={{ background: '#0f172a', color: 'white', padding: '2px 8px', borderRadius: 8, fontSize: 11 }}>
            Tier {slot.tier}
          </span>
        )}
      </div>
      <div style={{ fontSize: 14, fontWeight: 600, color: eligibleColor, marginBottom: 4 }}>
        {slot.eligible ? 'Eligible now' : `Eligible on ${formatDate(slot.eligible_on)}`}
      </div>
      <div style={{ fontSize: 13, color: '#475569' }}>
        {slot.reason}
      </div>
    </Card>
  );
}

export default function MyCareerPath() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;
    getMyCareerPath()
      .then(res => { if (mounted) setData(res.data); })
      .catch(err => {
        if (!mounted) return;
        setError(err.response?.data?.error || err.message || 'Failed to load.');
      })
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Loading your career path…</div>;
  if (error) return (
    <div style={{ padding: 24 }}>
      <div style={{ background: '#fee2e2', color: '#991b1b', padding: 12, borderRadius: 8 }}>{error}</div>
    </div>
  );
  if (!data) return null;

  const { eligibility, tracks, certifications, recommended_modules, cross_group_opportunities } = data;
  const cur = eligibility?.current || {};
  const groupStyle = GROUP_STYLE[cur.pay_group] || { color: '#475569', bg: '#f8fafc', label: 'Unplaced' };
  const dualTrack = !!eligibility?.dual_track;

  return (
    <div style={{ padding: '24px 28px', maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ marginBottom: 16 }}>
        <Link to="/training" style={{ color: '#475569', textDecoration: 'none', fontSize: 14, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <FiArrowLeft /> Back to Training
        </Link>
      </div>

      <h1 style={{ margin: 0, color: '#0f172a' }}>Your Career Path</h1>
      <p style={{ color: '#64748b', fontSize: 14, marginTop: 6, marginBottom: 24 }}>
        Where you are, how to advance, and what's expected. NGS career framework — effective 2026-05-07.
      </p>

      {/* === SECTION 1: WHERE YOU ARE === */}
      <Section title="Where you are now" icon={FiTarget} color={groupStyle.color}>
        <Card color={groupStyle.color} bg={groupStyle.bg}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, alignItems: 'flex-start' }}>
            <div style={{ flex: '1 1 220px' }}>
              <div style={{ fontSize: 11, color: groupStyle.color, fontWeight: 700, marginBottom: 4, letterSpacing: 0.5, textTransform: 'uppercase' }}>Group</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: '#0f172a' }}>{groupStyle.label}</div>
              {dualTrack && (
                <div style={{ marginTop: 6, display: 'inline-block', background: '#fef3c7', color: '#92400e', padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700 }}>
                  ★ DUAL-TRACK (Senior bracket auto-qualified)
                </div>
              )}
            </div>
            <div style={{ flex: '1 1 220px' }}>
              <div style={{ fontSize: 11, color: groupStyle.color, fontWeight: 700, marginBottom: 4, letterSpacing: 0.5, textTransform: 'uppercase' }}>Tier &amp; Bracket</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: '#0f172a' }}>
                {cur.pay_tier ? `Tier ${cur.pay_tier} ${cur.pay_bracket || ''}` : 'Not placed yet'}
              </div>
              {cur.pay_group && cur.pay_tier && cur.pay_bracket && (
                <div style={{ fontSize: 14, color: '#475569', marginTop: 4 }}>
                  {formatTierBracket(cur.pay_group, cur.pay_tier, cur.pay_bracket) || '—'}
                </div>
              )}
            </div>
            <div style={{ flex: '1 1 220px' }}>
              <div style={{ fontSize: 11, color: groupStyle.color, fontWeight: 700, marginBottom: 4, letterSpacing: 0.5, textTransform: 'uppercase' }}>Time at this placement</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: '#0f172a' }}>{formatDays(cur.days_in_tier)}</div>
              {cur.entered_at && (
                <div style={{ fontSize: 13, color: '#475569', marginTop: 4 }}>since {formatDate(cur.entered_at)}</div>
              )}
            </div>
          </div>
        </Card>

        {/* Track badges */}
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', marginBottom: 6, letterSpacing: 0.5, textTransform: 'uppercase' }}>
            Your tracks
          </div>
          {tracks.length === 0 && certifications.length === 0 ? (
            <div style={{ color: '#94a3b8', fontStyle: 'italic', fontSize: 14 }}>
              No tracks assigned yet. Talk to your manager about which track fits — Field (ST or IO) or Office (OT or CAD).
            </div>
          ) : (
            <div>
              {tracks.map(t => {
                const s = CAREER_TRACKS[t.career_track] || { color: '#475569', bg: '#f1f5f9' };
                return (
                  <span key={t.id} style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    background: s.bg, color: s.color, padding: '6px 12px',
                    borderRadius: 16, fontSize: 13, fontWeight: 600, marginRight: 8, marginBottom: 8
                  }}>
                    <FiStar size={14} /> {t.career_track} L{t.career_level}
                  </span>
                );
              })}
            </div>
          )}
        </div>

        {/* Earned exam certifications */}
        {certifications.length > 0 && (
          <div style={{ marginTop: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', marginBottom: 6, letterSpacing: 0.5, textTransform: 'uppercase' }}>
              Exam certifications earned
            </div>
            {certifications.map(c => <CertBadge key={c.module_id} cert={c} />)}
          </div>
        )}
      </Section>

      {/* === SECTION 2: HOW PAY WORKS === */}
      <Section
        title="How NGS pay works"
        icon={FiInfo}
        intro="Pay is by tier within group, with three brackets per tier (Entry → Mid → Senior). You move up the brackets by time in tier; you move up the tiers by certifications and time. Track-level (L1/L2/L3) determines what work you can do; tier determines pay."
      >
        {cur.pay_group ? (
          <PayTierTable group={cur.pay_group} highlightTier={cur.pay_tier} highlightBracket={cur.pay_bracket} />
        ) : (
          <div style={{ color: '#94a3b8', fontStyle: 'italic', fontSize: 14, padding: 12 }}>
            Once you're placed in a group, your tier/bracket position will highlight on the pay table here.
          </div>
        )}
      </Section>

      {/* === SECTION 3: NEXT STEPS === */}
      <Section
        title="Your path to next level"
        icon={FiTrendingUp}
        color="#0891b2"
        intro="Promotion has two flavors: bracket movement within your tier (Entry → Mid → Senior) and tier promotion to the next tier. Bracket comes first, then tier."
      >
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 14 }}>
          <NextStepCard slot={eligibility?.next_bracket} label="Next bracket" color="#0891b2" />
          <NextStepCard slot={eligibility?.next_tier} label="Next tier" color="#2563eb" />
        </div>
        {!eligibility?.next_bracket && !eligibility?.next_tier && (
          <Card>
            <div style={{ color: '#475569', fontSize: 14 }}>
              Once your tier and bracket are set, your eligibility for the next promotion will appear here.
            </div>
          </Card>
        )}
      </Section>

      {/* === SECTION 4: DUAL-TRACK BRACKET RULE === */}
      <Section
        title="The Dual-Track Bracket Rule"
        icon={FiAward}
        color="#92400e"
        intro="NGS rewards breadth across the firm. Holding a certification in the OTHER group (Field if you're in Office, or vice versa) automatically qualifies you for the Senior bracket at your current tier. Concrete pay impact, no waiting period."
      >
        {dualTrack ? (
          <Card color="#fde68a" bg="#fffbeb">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <FiCheckCircle color="#16a34a" size={20} />
              <span style={{ fontWeight: 700, color: '#92400e' }}>You're a dual-track tech</span>
            </div>
            <div style={{ fontSize: 14, color: '#475569' }}>
              You hold certifications in both Field and Office groups, so you're auto-qualified for the Senior bracket at your current tier. Continue advancing within both groups; consider the next-level certifications above.
            </div>
          </Card>
        ) : cross_group_opportunities.length > 0 ? (
          <Card color="#fde68a" bg="#fffbeb">
            <div style={{ marginBottom: 10, fontSize: 14, color: '#475569' }}>
              You're not yet dual-track. Earning any of the certifications below would auto-qualify you for the <strong>Senior bracket</strong> at your current tier:
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 10 }}>
              {cross_group_opportunities.map(opp => {
                const s = CAREER_TRACKS[opp.track] || { color: '#475569', bg: '#f1f5f9' };
                return (
                  <Link key={opp.module_id} to={`/training/modules/${opp.module_id}`} style={{
                    textDecoration: 'none',
                    background: 'white',
                    border: `1px solid ${s.color}`,
                    borderLeft: `4px solid ${s.color}`,
                    borderRadius: 8,
                    padding: 12,
                    color: '#0f172a',
                    display: 'block',
                  }}>
                    <div style={{ fontWeight: 700, color: s.color, marginBottom: 4 }}>{opp.track} L{opp.level}</div>
                    <div style={{ fontSize: 13, color: '#475569', marginBottom: 6 }}>{opp.title}</div>
                    <div style={{ fontSize: 12, color: '#0f172a', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      Start this module <FiArrowRight size={12} />
                    </div>
                  </Link>
                );
              })}
            </div>
          </Card>
        ) : (
          <Card>
            <div style={{ color: '#475569', fontSize: 14 }}>
              Once you have a track in either Field or Office, the cross-group opportunities will appear here.
            </div>
          </Card>
        )}
      </Section>

      {/* === SECTION 5: RECOMMENDED NEXT MODULES === */}
      <Section
        title="Recommended next training modules"
        icon={FiBookOpen}
        color="#3b82f6"
        intro="Direct path forward. These modules are the natural next step based on what you've already earned."
      >
        {recommended_modules.length === 0 ? (
          <Card>
            <div style={{ color: '#475569', fontSize: 14 }}>
              You're at the top of every track you hold. Talk to your manager about cross-group opportunities (Dual-Track Bracket Rule) or specialty pursuit.
            </div>
          </Card>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 }}>
            {recommended_modules.map(m => {
              const s = CAREER_TRACKS[m.track] || { color: '#475569', bg: '#f1f5f9' };
              return (
                <Link key={m.module_id} to={`/training/modules/${m.module_id}`} style={{
                  textDecoration: 'none',
                  background: 'white',
                  border: `1px solid ${s.color}`,
                  borderLeft: `4px solid ${s.color}`,
                  borderRadius: 8,
                  padding: 14,
                  color: '#0f172a',
                  display: 'block',
                }}>
                  <div style={{ fontWeight: 700, color: s.color, fontSize: 13, marginBottom: 4 }}>{m.track} L{m.level}</div>
                  <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>{m.title}</div>
                  <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8 }}>{m.reason}</div>
                  <div style={{ fontSize: 12, color: s.color, display: 'inline-flex', alignItems: 'center', gap: 4, fontWeight: 600 }}>
                    Open module <FiArrowRight size={12} />
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </Section>

      {/* === SECTION 6: THE LADDER === */}
      <Section
        title="The full ladder"
        icon={FiStar}
        color="#0f172a"
        intro="Six tiers in your group, three brackets per tier. Your current placement is highlighted. Tier 5 and Tier 6 require both tracks within your group (interweaving rule)."
      >
        <Ladder group={cur.pay_group} currentTier={cur.pay_tier} currentBracket={cur.pay_bracket} />
      </Section>

      {/* Footer note */}
      <div style={{
        background: '#f8fafc',
        border: '1px solid #e2e8f0',
        borderRadius: 8,
        padding: 14,
        fontSize: 13,
        color: '#475569',
        marginTop: 24
      }}>
        <strong>Have questions?</strong> Talk to your manager during your next review, or visit{' '}
        <Link to="/training" style={{ color: '#0891b2' }}>Training & Certification</Link> to start any module above.
        Pay placement and tier history are managed by Payroll/Office Manager;
        promotion eligibility is computed from your time-in-tier plus the rules described on this page.
      </div>
    </div>
  );
}

function Ladder({ group, currentTier, currentBracket }) {
  if (!group) {
    return (
      <div style={{ color: '#94a3b8', fontStyle: 'italic', fontSize: 14, padding: 12 }}>
        Once you're placed in a group, your full tier ladder will appear here.
      </div>
    );
  }
  const style = GROUP_STYLE[group];
  const tiers = [1, 2, 3, 4, 5, 6];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 8, marginTop: 4 }}>
      {tiers.map(t => {
        const isCurrent = currentTier === t;
        const isPast = currentTier && t < currentTier;
        const isFuture = currentTier && t > currentTier;
        const upperTier = t >= 5;
        return (
          <div key={t} style={{
            border: `2px solid ${isCurrent ? style.color : '#e2e8f0'}`,
            borderRadius: 8,
            padding: 10,
            textAlign: 'center',
            background: isCurrent ? style.bg : (isPast ? '#f0fdf4' : 'white'),
            opacity: isFuture ? 0.65 : 1,
            position: 'relative'
          }}>
            <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase' }}>
              Tier {t}
            </div>
            <div style={{
              fontSize: 13, fontWeight: 700,
              color: isCurrent ? style.color : '#0f172a',
              marginTop: 4
            }}>
              {isCurrent ? (currentBracket || 'Current') : (isPast ? '✓ Reached' : 'Future')}
            </div>
            {upperTier && (
              <div title="Upper tiers require both tracks within your group (interweaving rule)" style={{
                fontSize: 10, color: '#92400e', marginTop: 4,
                background: '#fef3c7', borderRadius: 4, padding: '2px 4px'
              }}>
                Interweaving
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
